import { Html } from '@react-three/drei';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { net, netState, subscribeNet } from '@/lib/net';

interface WhoAmI {
  colo: string;
  country: string;
  city?: string;
  build: string;
  ms: number;
  http: string;
}

// flat: screen-sized at a world point instead of a panel in the scene, for far cameras
export function HoloScreen({
  position,
  rotationY = 0,
  flat = false,
}: {
  position: [number, number, number];
  rotationY?: number;
  flat?: boolean;
}) {
  const [tab, setTab] = useState<'edge' | 'stack'>('edge');
  const offline = useSyncExternalStore(subscribeNet, netState).offline;
  const who = useWhoAmI(offline);

  return (
    <group position={position} rotation-y={rotationY}>
      <Html
        transform={!flat}
        center={flat}
        distanceFactor={flat ? undefined : 10}
        zIndexRange={[10, 0]}
        style={{ pointerEvents: 'auto' }}
      >
        <div className="holo">
          <div className="holo-bar">
            <span className="holo-dot" />
            <span>manudev · {who ? 'live' : offline ? 'offline mode' : 'edge offline'}</span>
          </div>
          <div className="holo-tabs">
            <button data-active={tab === 'edge'} onClick={() => setTab('edge')}>
              Edge
            </button>
            <button data-active={tab === 'stack'} onClick={() => setTab('stack')}>
              Stack
            </button>
          </div>
          {tab === 'edge' ? (
            <div className="holo-body">
              {who ? (
                <dl className="holo-kv">
                  <dt>served from</dt>
                  <dd>
                    {who.colo} · {who.city ? `${who.city}, ` : ''}
                    {who.country}
                  </dd>
                  <dt>build</dt>
                  <dd>{who.build}</dd>
                  <dt>function time</dt>
                  <dd>{who.ms.toFixed(1)} ms</dd>
                  <dt>protocol</dt>
                  <dd>{who.http}</dd>
                </dl>
              ) : offline ? (
                <p>Offline mode is on, so this screen does not ask the edge.</p>
              ) : (
                <p>
                  Deployed, this screen reports the Cloudflare edge location, build hash and
                  function time of the very page you are looking at. The dev server has no
                  functions, so it stays quiet.
                </p>
              )}
              <p className="holo-mono">GET /api/whoami · Pages Function</p>
            </div>
          ) : (
            <div className="holo-body">
              <p className="holo-mono">
                vite · react · r3f · ifc-lite → glTF
                <br />
                cloudflare pages · functions
              </p>
              <p>Every model here left Revit as IFC and was compressed by a custom pipeline.</p>
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}

function useWhoAmI(offline: boolean) {
  const [who, setWho] = useState<WhoAmI | null>(null);
  useEffect(() => {
    if (offline) return;
    const ctrl = new AbortController();
    net('/api/whoami', { signal: ctrl.signal })
      .then((r) => (r.ok ? (r.json() as Promise<WhoAmI>) : null))
      .then((w) => w && setWho(w))
      .catch(() => undefined);
    return () => ctrl.abort();
  }, [offline]);
  return offline ? null : who;
}
